import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from './config';
import { User, UserRole } from '../../types/user.types';

/**
 * Creates a user document with a custom ID while maintaining a reference to the Firebase Auth UID
 * This allows us to use custom IDs for invitation links while still being able to find users by Auth UID
 */
export const createUserWithCustomId = async (customId: string, userData: User) => {
  try {
    // Create the main user document with custom ID
    await setDoc(doc(db, 'users', customId), userData);

    // Auto-create roundRobinConsultants entry for eligible consultant roles
    const consultantRoles = [
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3
    ];

    if (userData.role && consultantRoles.includes(userData.role as UserRole)) {
      try {
        // New consultants default to OFF (isEligible=false) for round robin leads.
        // An HQ admin must explicitly enable them via the Round Robin Settings page
        // before they start receiving leads. This avoids new hires receiving leads
        // before they are trained / handed over.
        await setDoc(doc(db, 'roundRobinConsultants', customId), {
          id: customId,
          isEligible: false,
          isExcluded: false,
          currentBatchCount: 0,
          partnerId: userData.partnerId || userData.tenantId || 'propinvest_hq',
          updatedAt: Timestamp.now()
        });
        console.log('Created roundRobinConsultants entry (disabled by default) for:', customId);
      } catch (error) {
        console.error('Failed to create roundRobinConsultants entry:', error);
        // Don't fail user creation if this fails
      }
    }

    // Create a mapping document to link Firebase Auth UID to custom ID (when available)
    if (userData.uid && userData.uid !== customId) {
      await setDoc(doc(db, 'userMappings', userData.uid), {
        userId: customId,
        customId: customId,  // Keep for backward compatibility
        email: userData.email,
        createdAt: new Date()
      });
    }

    console.log('User document created successfully:', customId);
    return customId;
  } catch (error) {
    console.error('Error creating user with custom ID:', error);
    throw error;
  }
};

/**
 * Gets a user by Firebase Auth UID, checking both direct lookup and mapping
 */
export const getUserByAuthUid = async (authUid: string): Promise<User | null> => {
  try {
    // First try direct lookup (for users where doc ID = auth UID)
    const directDoc = await getDoc(doc(db, 'users', authUid));
    if (directDoc.exists()) {
      return { id: directDoc.id, ...directDoc.data() } as User;
    }
    
    // If not found, check mapping collection
    const mappingDoc = await getDoc(doc(db, 'userMappings', authUid));
    if (mappingDoc.exists()) {
      const mappingData = mappingDoc.data();
      // Try userId first (new field), fall back to customId (old field)
      const docId = mappingData.userId || mappingData.customId;
      const userDoc = await getDoc(doc(db, 'users', docId));
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() } as User;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by auth UID:', error);
    return null;
  }
};

/**
 * Updates the Firebase Auth UID for a user after they complete email link sign-in
 */
export const updateUserAuthUid = async (customId: string, authUid: string) => {
  try {
    // Update the user document with the Auth UID
    await setDoc(doc(db, 'users', customId), {
      uid: authUid
    }, { merge: true });
    
    // Create mapping if auth UID is different from custom ID
    if (authUid !== customId) {
      await setDoc(doc(db, 'userMappings', authUid), {
        userId: customId,
        customId: customId,  // Keep for backward compatibility
        updatedAt: new Date()
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error updating user auth UID:', error);
    throw error;
  }
};